import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

export interface RunButtonProps {
  /** Fired when the idle button is pressed. */
  onRun: () => void;
  disabled?: boolean;
  /** The action label, e.g. "Run". */
  runLabel: string;
  /**
   * When provided, the button toggles to a Stop control while `isStreaming` is
   * true (for modes that stream). Omit for one-shot modes (e.g. detect).
   */
  onStop?: () => void;
  isStreaming?: boolean;
  /** Stop label, e.g. "Stop". Shown only while streaming. */
  stopLabel?: string;
  /** Per-mode shape tweaks, e.g. a mobile pill via `rounded-full`. */
  className?: string;
}

/**
 * Shared run/submit control: a `Run ⌘↵` (desktop) / `Run` + send-icon (mobile)
 * button. Streaming modes pass `onStop` + `isStreaming` to get a Run⇄Stop toggle;
 * one-shot modes omit them. Presentational — each mode owns its run gate
 * (`disabled`), labels, and (for streaming modes) the stop wiring.
 */
const RunButton = ({
  onRun,
  disabled,
  runLabel,
  onStop,
  isStreaming = false,
  stopLabel,
  className,
}: RunButtonProps) => {
  const showStop = isStreaming && !!onStop;

  const handleRun = () => {
    onRun();
  };

  return (
    <Button
      className={cn(
        "relative flex cursor-pointer items-center justify-center rounded-full bg-foreground px-3 py-2 text-background label-2 sm:rounded-md",
        className,
      )}
      data-testid={showStop ? "stop-button" : "run-button"}
      disabled={disabled}
      onClick={showStop ? onStop : handleRun}
    >
      {onStop && (
        <span className={cn("flex items-center gap-1", showStop ? "opacity-100" : "opacity-0")}>
          {stopLabel}
          <div className="size-3.5 rounded-sm bg-background" />
        </span>
      )}
      <span
        className={cn(
          "flex items-center justify-center gap-2",
          onStop && "absolute inset-0",
          showStop ? "opacity-0" : "opacity-100",
        )}
      >
        <span className="hidden sm:block">{runLabel} ⌘↵</span>
        <span className="flex items-center gap-1 sm:hidden">
          {runLabel}
          <Icon className="size-4" name="send" />
        </span>
      </span>
    </Button>
  );
};

export default RunButton;
